import { glossaryById } from "./glossary";
import { postsBySlug } from "./registry";

export type CitedStat = {
  /** Short headline number or claim */
  headline: string;
  /** One-line context */
  context: string;
  sourceLabel: string;
  sourceUrl: string;
};

export type PostEnrichment = {
  heroImage: string;
  /** Second visual — mid-article */
  inlineImage: string;
  stats: CitedStat[];
  /** Optional line after intro with [[def:id|…]] links */
  introSupplement: string | null;
};

/** Curated Unsplash photo IDs — warehouse, retail, data, cold chain, teams (stable CDN). */
const UNSPLASH_IDS = [
  "1586528116311-ad8dd3c8310d",
  "1553413077-190dd305871c",
  "1600880292203-757bb62b4baf",
  "1503387765885-bbdd56c064be",
  "1556742049-0cfed4f6a45a",
  "1454165804606-c3d57bc86b40",
  "1552664730-d307ca884978",
  "1489515217757-5fd1be406fef",
  "1551434678-e076c223a692",
  "1521737604893-d14cc237f11d",
  "1556761175-b413da4baf72",
  "1542744173-8e7e5348bbda",
  "1581091226825-a6a2a5aee158",
  "1563986768609-322da13575f3",
  "1556761175-5973da0fbfd9",
  "1507679799986-c73749587adf",
  "1517245385007-279f0c4a3b3a",
  "1553877522-43269d4ea984",
  "1460925895917-afdab827c52f",
  "1551288049-bebda4e38f71",
];

function unsplashUrl(photoId: string, w = 1600): string {
  return `https://images.unsplash.com/photo-${photoId}?auto=format&fit=crop&w=${w}&q=85`;
}

function stableHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = Math.imul(31, h) + s.charCodeAt(i);
  return Math.abs(h);
}

/** Peer-reviewed / institutional sources for stat ribbons (verify periodically). */
const STAT_LIBRARY = {
  foodWasteIndex: {
    headline: "1.05B t",
    context: "Food wasted globally in 2022 at retail, food service & household (≈19% of food available to those sectors).",
    sourceLabel: "UNEP Food Waste Index Report 2024",
    sourceUrl: "https://www.unep.org/resources/report/unep-food-waste-index-report-2024",
  },
  faoSupplyLoss: {
    headline: "~13%",
    context: "Share of world’s food lost after harvest through retail (excl. retail/household waste) — supply-chain loss pressure.",
    sourceLabel: "FAO — Food loss and waste",
    sourceUrl: "https://www.fao.org/platform-food-loss-waste",
  },
  nrfShrink: {
    headline: "1.6%",
    context: "US retail shrink as % of sales in NRF’s 2023 survey (FY 2022) — industry benchmark; methodology & definitions vary by retailer.",
    sourceLabel: "NRF — National Retail Security Survey 2023",
    sourceUrl: "https://nrf.com/research/national-retail-security-survey-2023",
  },
  ghgFoodWaste: {
    headline: "8–10%",
    context: "Share of global GHG emissions from food that is lost and wasted (system-wide).",
    sourceLabel: "UNEP (Food Waste Index narrative)",
    sourceUrl: "https://www.unep.org/resources/report/unep-food-waste-index-report-2024",
  },
  workingCapital: {
    headline: "Cash tied up",
    context: "Inventory often represents 20–35%+ of total current assets for product companies — small % improvements move real cash.",
    sourceLabel: "McKinsey — Working capital",
    sourceUrl: "https://www.mckinsey.com/capabilities/strategy-and-corporate-finance/our-insights/how-companies-can-improve-working-capital-management",
  },
  forecastError: {
    headline: "Amplifies",
    context: "Forecast error compounds up the supply chain (bullwhip): ordering policies and lead times inflate swings vs end demand.",
    sourceLabel: "Wikipedia — Bullwhip effect (primer)",
    sourceUrl: "https://en.wikipedia.org/wiki/Bullwhip_effect",
  },
  traceability: {
    headline: "Recall speed",
    context: "Regulators expect rapid lot traceback — hours to days, not weeks — when lots are mixed or records are partial.",
    sourceLabel: "FDA — FSMA traceability (US context)",
    sourceUrl: "https://www.fda.gov/food/food-safety-modernization-act-fsma/fsma-final-rule-requirements-additional-traceability-records-certain-foods",
  },
  coldChain: {
    headline: "Temp excursions",
    context: "Temperature-sensitive lanes multiply spoilage risk; continuous monitoring reduces silent shrink before counts catch it.",
    sourceLabel: "WHO — vaccine cold chain (principles)",
    sourceUrl: "https://www.who.int/teams/immunization-vaccines-and-biologicals/delivery-systems/vaccine-cold-chain",
  },
} as const;

const CATEGORY_STAT_KEYS: Record<string, (keyof typeof STAT_LIBRARY)[]> = {
  FMCG: ["foodWasteIndex", "nrfShrink", "ghgFoodWaste"],
  Operations: ["nrfShrink", "forecastError", "workingCapital"],
  Founder: ["foodWasteIndex", "workingCapital", "forecastError"],
  Analytics: ["forecastError", "workingCapital", "nrfShrink"],
  "Supply chain": ["forecastError", "faoSupplyLoss", "workingCapital"],
  Systems: ["traceability", "nrfShrink", "workingCapital"],
  Risk: ["nrfShrink", "foodWasteIndex", "forecastError"],
  Retail: ["nrfShrink", "foodWasteIndex", "workingCapital"],
  Strategy: ["workingCapital", "forecastError", "ghgFoodWaste"],
  Warehouse: ["nrfShrink", "coldChain", "traceability"],
  Finance: ["workingCapital", "nrfShrink", "forecastError"],
  Compliance: ["traceability", "nrfShrink", "foodWasteIndex"],
};

const DEFAULT_KEYS: (keyof typeof STAT_LIBRARY)[] = ["nrfShrink", "foodWasteIndex", "workingCapital"];

function pickStatsForCategory(category: string, slug: string): CitedStat[] {
  const keys = CATEGORY_STAT_KEYS[category] ?? DEFAULT_KEYS;
  const h = stableHash(slug);
  const rotated = [...keys.slice(h % keys.length), ...keys].slice(0, 3);
  const seen = new Set<string>();
  const out: CitedStat[] = [];
  for (const k of rotated) {
    if (seen.has(k)) continue;
    seen.add(k);
    const s = STAT_LIBRARY[k];
    out.push({ headline: s.headline, context: s.context, sourceLabel: s.sourceLabel, sourceUrl: s.sourceUrl });
    if (out.length >= 3) break;
  }
  return out;
}

const TERM_BY_CATEGORY: Record<string, string[]> = {
  FMCG: ["shrink", "write-off", "fefo"],
  Operations: ["cycle-count", "perpetual-inventory", "safety-stock"],
  Founder: ["working-capital", "sku", "traceability"],
  Analytics: ["doh", "turnover", "abc-analysis"],
  "Supply chain": ["bullwhip", "vmi", "moq"],
  Systems: ["perpetual-inventory", "traceability", "sku"],
  Risk: ["shrink", "stockout", "write-off"],
  Retail: ["stockout", "slob", "doh"],
  Strategy: ["working-capital", "jit", "jic"],
  Warehouse: ["cross-dock", "quarantine", "3pl"],
  Finance: ["working-capital", "write-off", "moq"],
  Compliance: ["traceability", "quarantine", "write-off"],
};

function buildIntroSupplement(slug: string, category: string): string | null {
  const ids = TERM_BY_CATEGORY[category] ?? ["shrink", "working-capital", "sku"];
  const h = stableHash(slug);
  const a = ids[h % ids.length]!;
  const b = ids[(h + 1) % ids.length]!;
  const c = ids[(h + 2) % ids.length]!;
  const uniq = [...new Set([a, b, c])].filter((id) => glossaryById[id]);
  if (uniq.length === 0) return null;
  const parts = uniq.map((id) => {
    const t = glossaryById[id]!.title.split("(")[0]!.trim();
    return `[[def:${id}|${t}]]`;
  });
  return `Key terms in this guide: ${parts.join(", ")}.`;
}

export function getPostEnrichment(slug: string): PostEnrichment | null {
  const meta = postsBySlug[slug];
  if (!meta) return null;
  const h = stableHash(slug);
  const hi = h % UNSPLASH_IDS.length;
  const heroImage = unsplashUrl(UNSPLASH_IDS[hi]!);
  const inlineImage = unsplashUrl(UNSPLASH_IDS[(hi + 7) % UNSPLASH_IDS.length]!);
  return {
    heroImage,
    inlineImage,
    stats: pickStatsForCategory(meta.category, slug),
    introSupplement: buildIntroSupplement(slug, meta.category),
  };
}
